import {THRESHOLD_FOR_ABOUT_TO_START} from '../../../constants';

import {useEffect, useState} from 'react';

export type EventState =
  | 'pending'
  | 'about-to-start'
  | 'in-progress'
  | 'completed';

const getEventState = (
  secondsToStart: number,
  durationSeconds: number,
): EventState => {
  if (secondsToStart > THRESHOLD_FOR_ABOUT_TO_START) return 'pending';
  else if (secondsToStart <= THRESHOLD_FOR_ABOUT_TO_START && secondsToStart > 0)
    return 'about-to-start';
  else if (secondsToStart <= 0 && Math.abs(secondsToStart) <= durationSeconds)
    return 'in-progress';
  else return 'completed';
};

/**
 * A custom React hook that manages the state of an event based on its start time, duration,
 * and a threshold for when the event is considered "about to start."
 *
 * @param startTimeUTCSeconds - The start time of the event in UTC seconds.
 * @param durationSeconds - The duration of the event in seconds.
 * @returns An object containing the current state of the event (`eventState`), which can be:
 *          - `'pending'`: The event is not starting soon (beyond the threshold).
 *          - `'about-to-start'`: The event is starting soon (within the threshold).
 *          - `'in-progress'`: The event has started and is still ongoing.
 *          - `'completed'`: The event has finished.
 *
 * @remarks
 * This hook uses `useEffect` to set up a timer that updates the event state every second.
 * The timer is cleared when the component using this hook is unmounted or when the
 * dependencies (`startTimeUTCSeconds`, `durationSeconds`, or `thresholdForAboutToStart`) change.
 *
 * @example
 * ```tsx
 * const {eventState} = useEventState(1672531200, 7200, 3600);
 * console.log(eventState); // Outputs the current state of the event
 * ```
 */
const useEventState = (
  startTimeUTCSeconds: number,
  durationSeconds: number,
) => {
  const nowUTCSeconds = Math.round(new Date().getTime() / 1000);
  let secondsToStart = startTimeUTCSeconds - nowUTCSeconds;
  const initialState = getEventState(secondsToStart, durationSeconds);

  const [eventState, setEventState] = useState<EventState>(initialState);

  useEffect(() => {
    if (eventState === 'completed') return;

    const timer = setInterval(() => {
      secondsToStart--;
      const eventState = getEventState(secondsToStart, durationSeconds);
      setEventState(eventState);
      if (eventState === 'completed') {
        clearInterval(timer);
        return;
      }
    }, 1000);

    return () => clearInterval(timer);
  // The interval will start once the hook is called, we don't need to update the useEffect after that.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {eventState};
};

export default useEventState;
